import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../api';
import QrPaymentModal from './QrPaymentModal';

const parseResourceKind = (lecture) => {
  const description = lecture?.description || '';
  const match = description.match(/^\[resource:([a-z]+)\]/i);
  return match?.[1]?.toLowerCase() || 'file';
};

const cleanLectureDescription = (lecture) =>
  (lecture?.description || '').replace(/^\[resource:[a-z]+\]\s*/i, '').trim();

const resourceTypeLabels = {
  file: 'Archivo',
  drive: 'Google Drive',
  github: 'GitHub',
  link: 'Enlace externo'
};

const getApiOrigin = () => {
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  return process.env.REACT_APP_API_ORIGIN || apiUrl.replace(/\/api\/?$/, '');
};

const CoursePlayer = ({ courseId, onNavigate }) => {
  const { currentUser, updateCurrentUser } = useAuth();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [currentLecture, setCurrentLecture] = useState(null);

  const getVideoUrl = (url) => {
    if (!url || typeof url !== 'string' || url.trim() === '') {
      return null;
    }

    if (url.startsWith('http') || url.startsWith('https')) {
      return url;
    }

    const cleanPath = url.startsWith('/') ? url.slice(1) : url;
    const apiOrigin = getApiOrigin();
    return `${apiOrigin}/${cleanPath}`;
  };

  const getYoutubeEmbedUrl = (url) => {
    if (!url || typeof url !== 'string') {
      return null;
    }

    const trimmedUrl = url.trim();
    const youtubePatterns = [
      /(?:youtube\.com\/watch\?v=)([^&]+)/i,
      /(?:youtu\.be\/)([^?&]+)/i,
      /(?:youtube\.com\/embed\/)([^?&]+)/i
    ];

    for (const pattern of youtubePatterns) {
      const match = trimmedUrl.match(pattern);
      if (match?.[1]) {
        return `https://www.youtube.com/embed/${match[1]}`;
      }
    }

    return null;
  };

  const getIframeEmbedUrl = (rawValue) => {
    if (!rawValue || typeof rawValue !== 'string') {
      return null;
    }

    const trimmedValue = rawValue.trim();
    const iframeMatch = trimmedValue.match(/<iframe[^>]+src=["']([^"']+)["']/i);
    if (iframeMatch?.[1]) {
      return iframeMatch[1];
    }

    const lowerValue = trimmedValue.toLowerCase();
    const isOneDriveLike = (
      lowerValue.includes('1drv.ms') ||
      lowerValue.includes('onedrive.live.com') ||
      lowerValue.includes('sharepoint.com')
    );

    if (isOneDriveLike) {
      return trimmedValue;
    }

    return null;
  };

  useEffect(() => {
    if (!courseId) {
      setError('No se especifico un curso');
      setLoading(false);
      onNavigate('home');
    }
  }, [courseId, onNavigate]);

  const loadCourse = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const existsRes = await api.get(`/courses/${courseId}/exists`);
      if (!existsRes.data?.exists) {
        throw new Error('Curso no encontrado en el sistema');
      }

      if (currentUser?.role === 'student') {
        const enrollmentRes = await api.get(`/courses/${courseId}/enrollment`);
        const isEnrolled = enrollmentRes.data?.data?.isEnrolled;

        if (!isEnrolled) {
          const basicRes = await api.get(`/courses/${courseId}/basic`);
          const basicData = basicRes.data.success ? basicRes.data.data : basicRes.data;

          if (basicData.price > 0) {
            setShowPaymentModal(true);
            setLoading(false);
            return;
          }
        }
      }

      const fullRes = await api.get(`/courses/${courseId}/full`);

      if (!fullRes.data || (!fullRes.data.success && !fullRes.data.id)) {
        throw new Error(fullRes.data?.message || 'Error al cargar el contenido del curso');
      }

      const fullData = fullRes.data.success ? fullRes.data.data : fullRes.data;
      setCourse(fullData);

      let lectureToLoad = currentLecture;
      if (!lectureToLoad) {
        lectureToLoad = fullData.sections
          ?.flatMap((section) => section.lectures || [])
          .find((lecture) => lecture);

        if (lectureToLoad) {
          setCurrentLecture(lectureToLoad);
        }
      }
    } catch (err) {
      console.error('Detalle del error loadCourse:', err);
      setError(err.response?.data?.message || err.message || 'Error al cargar el curso');
    } finally {
      setLoading(false);
    }
  }, [courseId, currentUser, currentLecture]);

  const handlePaymentSuccess = useCallback(() => {
    setShowPaymentModal(false);
    loadCourse();
    updateCurrentUser();
  }, [loadCourse, updateCurrentUser]);

  useEffect(() => {
    loadCourse();
  }, [loadCourse]);

  const handleLectureSelect = (lecture) => {
    setCurrentLecture(lecture);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-500 p-4 my-4 container mx-auto">
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  if (!course) {
    return <div className="text-center py-8">Curso no encontrado.</div>;
  }

  const isResourceLecture = currentLecture && currentLecture.content_type !== 'video';
  const resourceKind = isResourceLecture ? parseResourceKind(currentLecture) : null;
  const resourceUrl = currentLecture ? (currentLecture.content_url || '').trim() : '';
  const youtubeEmbedUrl = currentLecture ? getYoutubeEmbedUrl(currentLecture.content_url) : null;
  const iframeEmbedUrl = currentLecture ? getIframeEmbedUrl(currentLecture.content_url) : null;
  const videoSrc = currentLecture ? getVideoUrl(currentLecture.content_url) : null;
  const canPlayYoutube = currentLecture && currentLecture.content_type === 'video' && youtubeEmbedUrl;
  const canPlayIframe = currentLecture && currentLecture.content_type === 'video' && !youtubeEmbedUrl && iframeEmbedUrl;
  const canPlayDirectVideo = currentLecture && currentLecture.content_type === 'video' && videoSrc && !youtubeEmbedUrl && !iframeEmbedUrl;

  return (
    <div className="flex flex-col lg:flex-row gap-6 p-4">
      {showPaymentModal && (
        <QrPaymentModal
          course={course || { id: courseId, price: '...' }}
          onClose={() => setShowPaymentModal(false)}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}

      <div className="lg:w-2/3">
        <div className="bg-black rounded-lg overflow-hidden aspect-video relative flex items-center justify-center">
          {isResourceLecture ? (
            <div className="w-full h-full bg-slate-950 text-white p-8 flex flex-col items-center justify-center text-center">
              <div className="mb-4 text-sm uppercase tracking-[0.2em] text-blue-300">
                {resourceTypeLabels[resourceKind] || 'Recurso'}
              </div>
              <h3 className="text-3xl font-bold mb-3">{currentLecture.title}</h3>
              <p className="text-slate-300 max-w-xl mb-6">
                {cleanLectureDescription(currentLecture) || 'Este material complementario esta disponible para abrir o descargar.'}
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <a
                  href={resourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-5 py-3 rounded-lg bg-blue-600 hover:bg-blue-700"
                >
                  Abrir recurso
                </a>
                <a
                  href={resourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  download={resourceKind === 'file' ? true : undefined}
                  className="px-5 py-3 rounded-lg border border-slate-600 hover:bg-slate-800"
                >
                  {resourceKind === 'file' ? 'Descargar' : 'Acceder'}
                </a>
              </div>
              <p className="mt-6 text-xs text-slate-400 break-all max-w-2xl">{resourceUrl}</p>
            </div>
          ) : canPlayYoutube ? (
            <iframe
              key={currentLecture.id}
              src={youtubeEmbedUrl}
              title={currentLecture.title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          ) : canPlayIframe ? (
            <iframe
              key={currentLecture.id}
              src={iframeEmbedUrl}
              title={currentLecture.title}
              className="w-full h-full"
              allow="autoplay; fullscreen; picture-in-picture"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          ) : canPlayDirectVideo ? (
            <video
              key={currentLecture.id}
              src={videoSrc}
              className="w-full h-full"
              controls
              autoPlay
              controlsList="nodownload"
            />
          ) : (
            <div className="p-8 text-center text-white flex flex-col items-center">
              <svg className="w-16 h-16 mb-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              <h3 className="text-xl font-bold mb-2">
                {currentLecture ? 'Video no disponible' : 'Selecciona una leccion'}
              </h3>
              <p className="text-gray-400 text-sm">
                {currentLecture ? 'La URL del video parece estar vacia o invalida.' : 'Haz clic en el menu derecho para comenzar.'}
              </p>
            </div>
          )}
        </div>

        {currentLecture && (
          <div className="mt-4 bg-white p-4 rounded shadow-sm">
            <h1 className="text-2xl font-bold text-gray-800">{currentLecture.title}</h1>
            <p className="text-gray-600 mt-2">{cleanLectureDescription(currentLecture)}</p>
            {isResourceLecture ? (
              <div className="mt-4 flex flex-wrap gap-3">
                <a
                  href={resourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
                >
                  Abrir recurso
                </a>
                <a
                  href={resourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  download={resourceKind === 'file' ? true : undefined}
                  className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50"
                >
                  {resourceKind === 'file' ? 'Descargar' : 'Ir al enlace'}
                </a>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <div className="lg:w-1/3 bg-white rounded-lg shadow-sm overflow-hidden flex flex-col h-[calc(100vh-100px)]">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="font-bold text-lg">{course.title}</h3>
          <p className="text-sm text-gray-500">Contenido del curso</p>
        </div>
        <div className="overflow-y-auto flex-1">
          {course.sections?.length > 0 ? (
            course.sections.map((section) => (
              <div key={section.id} className="border-b last:border-b-0">
                <div className="p-3 bg-gray-100 font-medium text-sm text-gray-700">
                  {section.title}
                </div>
                <ul>
                  {section.lectures?.map((lecture) => {
                    const isResource = lecture.content_type !== 'video';
                    return (
                      <li
                        key={lecture.id}
                        onClick={() => handleLectureSelect(lecture)}
                        className={`p-3 border-l-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                          currentLecture?.id === lecture.id
                            ? 'bg-blue-50 border-blue-500 text-blue-700'
                            : 'border-transparent text-gray-600'
                        }`}
                      >
                        <div className="flex items-center text-sm">
                          <span className="mr-3">
                            {isResource ? (
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V8l-6-5H4z" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                              </svg>
                            )}
                          </span>
                          <span>{lecture.title}</span>
                        </div>
                      </li>
                    );
                  })}
                  {(!section.lectures || section.lectures.length === 0) && (
                    <li className="p-3 text-xs text-gray-400 italic pl-8">Sin lecciones</li>
                  )}
                </ul>
              </div>
            ))
          ) : (
            <div className="p-4 text-center text-gray-500">No hay contenido.</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoursePlayer;
